import React, { useState } from 'react';
import type { MaintenancePart } from '../../hooks/useMachineHub';
import { HubApiError, partsByMachine } from '../../services/hubApi';
import { humanizePartFunction } from '../MachineHub/partsQuote';
import {
  MAX_ITEM_QUANTITY,
  PartsOrderApiError,
  PartsOrderCartItem,
  WidgetAccount,
  WidgetSearchResult,
  copyFor,
  formatCurrency,
  searchPartNumber,
  toCartItem,
} from '../../services/partsOrderApi';
import { displayPartNumber } from '../../utils/displayPartNumber';

export interface ShopMachine {
  machine_id: number;
  model: string;
  make: string | null;
  category: string | null;
  customer_machine_id: number | null;
}

interface CatalogPart { part_number: string; description: string | null; image_url: string | null; list_price: number | null }

interface Props {
  organizationId: string;
  codeId: number | null;
  session: string | null;
  machine?: ShopMachine;
  maintenanceParts?: MaintenancePart[];
  onAdd: (item: PartsOrderCartItem) => void;
  onVerificationExpired: () => void;
  onAccounts?: (accounts: WidgetAccount[]) => void;
}

type Check = { state: 'idle' } | { state: 'loading' } | { state: 'found'; result: WidgetSearchResult } | { state: 'not_found' } | { state: 'error'; message: string };

/** "Check price and stock" -> live IntelliDealer price + Add. Shared by both sections. */
const PriceCheck: React.FC<{
  organizationId: string; codeId: number | null; session: string | null; partNumber: string; imageUrl: string | null; quantity?: number;
  onAdd: Props['onAdd']; onVerificationExpired: () => void; onAccounts?: Props['onAccounts'];
}> = ({ organizationId, codeId, session, partNumber, imageUrl, quantity, onAdd, onVerificationExpired, onAccounts }) => {
  const [check, setCheck] = useState<Check>({ state: 'idle' });

  const run = async () => {
    if (codeId == null) return;
    setCheck({ state: 'loading' });
    try {
      const result = await searchPartNumber(organizationId, codeId, partNumber, session);
      if (result.accounts) onAccounts?.(result.accounts);
      setCheck(result.found ? { state: 'found', result } : { state: 'not_found' });
    } catch (err) {
      if (err instanceof PartsOrderApiError && err.code === 'verification_required') { onVerificationExpired(); return; }
      setCheck({ state: 'error', message: err instanceof Error ? err.message : copyFor(undefined) });
    }
  };

  if (check.state === 'found') {
    const r = check.result;
    const price = r.customer_unit_price ?? r.list_price;
    return (
      <span className="hub-part-row__check">
        <span>{price != null ? formatCurrency(price) : 'Call for price'} · {r.quantity_across_all > 0 ? `${r.quantity_across_all} in stock` : 'Order in'}</span>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => onAdd({ ...toCartItem(r, imageUrl), quantity: Math.min(MAX_ITEM_QUANTITY, quantity ?? 1) })}>Add</button>
      </span>
    );
  }
  if (check.state === 'not_found') return <span className="hub-part-row__note">Not stocked, ask for a quote</span>;
  if (check.state === 'error') return <span className="error-text">{check.message}</span>;
  return (
    <button type="button" className="btn btn-secondary btn-sm" onClick={run} disabled={check.state === 'loading' || codeId == null}>
      {check.state === 'loading' ? 'Checking…' : 'Check price and stock'}
    </button>
  );
};

/** Two collapsed disclosures under the part-number search: catalog parts for the model, and maintenance quick picks. */
const MachinePartsSections: React.FC<Props> = ({ organizationId, codeId, session, machine, maintenanceParts, onAdd, onVerificationExpired, onAccounts }) => {
  const [catalog, setCatalog] = useState<{ parts: CatalogPart[] } | { error: string } | null>(null);
  const priceProps = { organizationId, codeId, session, onAdd, onVerificationExpired, onAccounts };

  // Lazy: the by-machine lookup only fires the first time the disclosure opens.
  const loadCatalog = (e: React.SyntheticEvent<HTMLDetailsElement>) => {
    if (!e.currentTarget.open || catalog || !machine) return;
    partsByMachine(organizationId, machine.machine_id)
      .then((res) => setCatalog({ parts: res.parts }))
      .catch((err) => setCatalog({ error: err instanceof HubApiError ? err.message : 'Lookup failed. Try again.' }));
  };

  return (
    <div className="hub-parts">
      {machine && (
        <details onToggle={loadCatalog}>
          <summary>Parts for your {machine.model}</summary>
          {!catalog && <p className="hub-part-row__note">Loading…</p>}
          {catalog && 'error' in catalog && <p className="error-text">{catalog.error}</p>}
          {catalog && 'parts' in catalog && catalog.parts.length === 0 && <p className="hub-part-row__note">No catalog matches for this model yet.</p>}
          {catalog && 'parts' in catalog && catalog.parts.map((p) => (
            <div className="hub-part-row" key={p.part_number}>
              {p.image_url && <img src={p.image_url} alt="" />}
              <span className="hub-part-row__text">
                <strong>{displayPartNumber(p.part_number)}</strong>
                {p.description && <span>{p.description}</span>}
                {p.list_price != null && <span className="hub-part-row__note">List {formatCurrency(p.list_price)}</span>}
              </span>
              <PriceCheck {...priceProps} partNumber={p.part_number} imageUrl={p.image_url} />
            </div>
          ))}
        </details>
      )}
      {maintenanceParts && maintenanceParts.length > 0 && (
        <details>
          <summary>Maintenance parts</summary>
          {maintenanceParts.map((p, i) => (
            <div className="hub-part-row" key={`${p.oem_part_number ?? p.part_function}-${i}`}>
              <span className="hub-part-row__text">
                <strong>{humanizePartFunction(p.part_function)}</strong>
                <span>
                  {displayPartNumber(p.oem_part_number) || 'No part number on file'}
                  {p.quantity != null && p.quantity > 1 && ` · qty ${p.quantity}`}
                  {p.interval_ref && ` · ${p.interval_ref}`}
                </span>
              </span>
              {p.oem_part_number
                ? <PriceCheck {...priceProps} partNumber={p.oem_part_number} imageUrl={null} quantity={p.quantity ?? 1} />
                : <span className="hub-part-row__note">Ask the parts desk</span>}
            </div>
          ))}
        </details>
      )}
    </div>
  );
};

export default MachinePartsSections;
